import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../components/ui/Logo';
import { apiClient } from '../services/api';
import { useAuth } from '../context/AuthContext';

type AuthStep = 'email' | 'code' | 'profile-prompt';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, verifyCode } = useAuth();

  const isRegisteringFromState = (location.state as any)?.isRegistering;
  const [isRegistering, setIsRegistering] = useState(isRegisteringFromState || false);

  // If user navigates here with intent to register, update local state
  useEffect(() => {
    if (isRegisteringFromState) {
      setIsRegistering(true);
    }
  }, [isRegisteringFromState]);

  // State
  const [step, setStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [remember, setRemember] = useState(true);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user && step !== 'profile-prompt') {
      const from = (location.state as any)?.from?.pathname || '/';
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate(from);
      }
    }
  }, [isAuthenticated, user, navigate, location, step]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleRequestCode = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await apiClient.requestLoginCode(email, isRegistering);

      setStep('code');
      setSuccess(`Verification code sent to ${email}`);
      setCountdown(60); // 60 second cooldown before resend
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to send verification code';
      setError(errorMsg);
      if (errorMsg.toLowerCase().includes('register first')) {
        setTimeout(() => {
          setIsRegistering(true); // Switch to registration form mentally
          navigate('/register');
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await verifyCode(email, code, remember);

      setSuccess('Login successful!');

      if (response.isNewUser) {
        setStep('profile-prompt' as any);
      } else {
        setTimeout(() => {
          // Just go back to where they were originally heading, or dashboard
          const from = (location.state as any)?.from?.pathname || '/';
          navigate(from, { replace: true });
        }, 1000);
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      setError(errorData?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setStep('email');
    setCode('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-x-hidden relative">
      {/* Wood Texture Background */}
      <div
        className="absolute inset-0 z-0 opacity-40 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=1280&q=60&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          filter: 'contrast(1.1) brightness(0.7)'
        }}
      />

      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />

      {/* Header */}
      <header className="relative z-50 flex items-center justify-between px-3 sm:px-6 py-4 sm:py-6 md:px-12 max-w-7xl mx-auto border-b border-premium-gold/20">
        <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <Logo variant="sidebar" size="sm" />
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <p className="hidden sm:block text-sm text-gray-400">
            {isRegistering
              ? (step === 'email' ? 'Already have an account?' : 'Having trouble?')
              : (step === 'email' ? 'Need an account?' : 'Having trouble?')}
          </p>
          <button
            onClick={() => {
              if (isRegistering) {
                setIsRegistering(false);
                setStep('email');
                navigate('/login', { replace: true, state: {} });
              } else {
                step === 'email' ? navigate('/register') : handleChangeEmail();
              }
            }}
            className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all transform hover:scale-105 active:scale-95 text-sm"
          >
            {isRegistering ? 'Log In' : (step === 'email' ? 'Sign Up' : 'Back')}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 py-24 flex items-center justify-center min-h-[80vh]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-md"
        >
          {/* Auth Card */}
          <div className="relative p-8 border border-premium-gold/30 rounded-2xl bg-gradient-to-br from-white/5 to-transparent backdrop-blur-xl overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-premium-gold/5 to-transparent opacity-50"></div>

            <div className="relative z-10">
              <AnimatePresence mode="wait">
                {step === 'email' ? (
                  <motion.div
                    key="email-step"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="text-center mb-8">
                      <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-premium-gold-light to-white bg-clip-text text-transparent">
                        {isRegistering ? 'Create Account' : 'Welcome Back'}
                      </h1>
                      <p className="text-gray-400">
                        {isRegistering ? 'Enter your email to get started' : 'Enter your email to receive a login code'}
                      </p>
                    </div>

                    <form onSubmit={handleRequestCode} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          required
                          disabled={loading}
                          className="w-full px-4 py-3 bg-white/5 border border-premium-gold/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-transparent transition-all disabled:opacity-50"
                        />
                      </div>

                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg"
                        >
                          <p className="text-sm text-red-400">{error}</p>
                        </motion.div>
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {loading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Sending Code...
                          </span>
                        ) : (
                          'Continue'
                        )}
                      </button>

                      <div className="text-center">
                        <p className="text-xs text-gray-500">
                          🔒 Passwordless authentication powered by email codes
                        </p>
                      </div>
                    </form>
                  </motion.div>
                ) : step === 'code' ? (
                  <motion.div
                    key="code-step"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="text-center mb-8">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-premium-gold/20 rounded-full mb-4">
                        <svg className="w-8 h-8 text-premium-gold-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-premium-gold-light to-white bg-clip-text text-transparent">
                        Check Your Email
                      </h1>
                      <p className="text-gray-400">
                        We sent a 6-digit code to
                      </p>
                      <p className="text-premium-gold-light font-semibold mt-1">
                        {email}
                      </p>
                    </div>

                    <form onSubmit={handleVerifyCode} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2 text-center">
                          Verification Code
                        </label>
                        <input
                          type="text"
                          value={code}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setCode(value);
                          }}
                          placeholder="000000"
                          maxLength={6}
                          required
                          disabled={loading}
                          autoFocus
                          className="w-full px-4 py-4 bg-white/5 border border-premium-gold/30 rounded-lg text-white text-center text-2xl font-mono tracking-widest placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-transparent transition-all disabled:opacity-50"
                        />
                        <p className="text-xs text-gray-500 text-center mt-2">
                          Code expires in 10 minutes
                        </p>
                      </div>

                      <div className="flex items-center gap-2 px-1">
                        <input
                          type="checkbox"
                          id="remember-me"
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                          className="w-4 h-4 rounded border-premium-gold/30 bg-white/5 text-premium-gold focus:ring-premium-gold/50 cursor-pointer"
                        />
                        <label htmlFor="remember-me" className="text-sm text-gray-300 cursor-pointer select-none">
                          Stay logged in on this device
                        </label>
                      </div>

                      <div className="p-3 bg-premium-gold/10 border border-premium-gold/30 rounded-lg text-center">
                        <p className="text-sm text-premium-gold-light font-medium">
                          ⚠️ If the code is not in your inbox, please check your spam folder.
                        </p>
                      </div>

                      {success && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg"
                        >
                          <p className="text-sm text-green-400 text-center">{success}</p>
                        </motion.div>
                      )}

                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg"
                        >
                          <p className="text-sm text-red-400 text-center">{error}</p>
                        </motion.div>
                      )}

                      <button
                        type="submit"
                        disabled={loading || code.length !== 6}
                        className="w-full py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {loading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Verifying...
                          </span>
                        ) : (
                          'Verify & Login'
                        )}
                      </button>

                      <div className="flex flex-col gap-3">
                        <button
                          type="button"
                          onClick={handleChangeEmail}
                          className="text-sm text-gray-400 hover:text-premium-gold-light transition-colors"
                        >
                          ← Change Email Address
                        </button>

                        <button
                          type="button"
                          onClick={handleRequestCode}
                          disabled={countdown > 0 || loading}
                          className="text-sm text-premium-gold-light hover:text-premium-gold-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {countdown > 0
                            ? `Resend Code (${countdown}s)`
                            : '🔄 Resend Code'
                          }
                        </button>
                      </div>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div
                    key="profile-prompt-step"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="text-center mb-8">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-premium-gold/20 rounded-full mb-4">
                        <svg className="w-8 h-8 text-premium-gold-light" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-premium-gold-light to-white bg-clip-text text-transparent">
                        Setup Your Profile
                      </h1>
                      <p className="text-gray-400">
                        Welcome to TechUnity! Would you like to set up your professional profile now to get matched with better opportunities?
                      </p>
                    </div>

                    <div className="space-y-4">
                      <button
                        type="button"
                        onClick={() => navigate('/settings')}
                        className="w-full py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] transition-all transform hover:scale-105 active:scale-95"
                      >
                        Yes, Set Up Profile
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const from = (location.state as any)?.from?.pathname || '/';
                          navigate(from, { replace: true });
                        }}
                        className="w-full py-3 bg-white/5 border border-premium-gold/30 text-white font-bold rounded-lg hover:bg-white/10 transition-all transform hover:scale-105 active:scale-95"
                      >
                        Skip for Now
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Info Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 text-center"
          >
            <div className="p-6 border border-premium-gold/20 rounded-xl bg-white/5 backdrop-blur">
              <div className="flex items-center justify-center gap-2 mb-3">
                <svg className="w-5 h-5 text-premium-gold-light" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <h3 className="text-sm font-semibold text-premium-gold-light">Passwordless Security</h3>
              </div>
              <p className="text-xs text-gray-400">
                No passwords to remember or manage. Login securely with email verification codes that expire after 10 minutes.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </main>
    </div >
  );
};

export default Login;